import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { Sizes } from '@/components/ui/types';

import { DockItemProps, DockLabelProps, DockProps, DockVariant } from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'dock-xs',
  sm: 'dock-sm',
  md: 'dock-md',
  lg: 'dock-lg',
  xl: 'dock-xl',
};

const variantClasses: Record<DockVariant, string> = {
  default: '',
  floating: twMerge(
    'inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] h-16 w-auto',
    'justify-center gap-4 px-4 pt-3 pb-2',
    'rounded-full border-0 bg-base-100/30 text-base-content/50 backdrop-blur-[10px]',
    '*:mb-0 *:w-16 *:min-w-0 *:shrink *:basis-auto *:gap-1 *:after:hidden',
    '[&_.dock-label]:max-w-full [&_.dock-label]:truncate [&_.dock-label]:text-xs [&_.dock-label]:leading-5',
  ),
};

const DockBase = forwardRef<HTMLElement, DockProps>(
  (
    { className, size = 'md', variant = 'default', children, ...props },
    ref,
  ) => {
    const classes = twMerge(
      'dock',
      sizeClasses[size],
      variantClasses[variant],
      className,
    );

    return (
      <nav ref={ref} className={classes} {...props}>
        {children}
      </nav>
    );
  },
);

const Item = forwardRef<HTMLAnchorElement, DockItemProps>(
  ({ className, active = false, href, children, ...props }, ref) => (
    <Link
      ref={ref}
      to={href}
      className={twMerge(active && 'dock-active text-primary', className)}
      aria-current={active ? 'page' : undefined}
      {...props}
    >
      {children}
    </Link>
  ),
);

const Label = forwardRef<HTMLSpanElement, DockLabelProps>(
  ({ className, children, ...props }, ref) => (
    <span ref={ref} className={twMerge('dock-label', className)} {...props}>
      {children}
    </span>
  ),
);

DockBase.displayName = 'Dock';
Item.displayName = 'Dock.Item';
Label.displayName = 'Dock.Label';

export const Dock = Object.assign(DockBase, {
  Item,
  Label,
});
